import { FC, ReactNode } from 'react'
import { useHolidaysOnDate } from '@hooks/useHolidays'
import { useAbsencesForDate } from '@hooks/useAbsences'
import { CellAnnotation, TimeStampCell } from 'components/tables'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarStar,
  faCloudCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { getApprovalStatus } from '@features/approvals/helpers/getApprovalStatus'
import { twMerge } from '@lib/tailwind-merge'
import { Status } from 'types'
import { ApprovalTooltip } from './ApprovalTooltip'
import { secondsToTimestamp } from '@utils/time'
import { useNoteForDate } from '@hooks/useNotes'
import { useWeekScope } from '@hooks/useWeekScope'

interface Props {
  approval?: Omit<Approval, 'approvalDomain'>
  className?: string
  date: DateTime
  holidayCalendarId?: string
  userId: number
}

export const DayCell: FC<Props> = ({
  approval,
  className,
  date,
  holidayCalendarId,
  userId,
}) => {
  const { end, start } = useWeekScope()
  const holidayNames = useHolidaysOnDate(date, {
    holidayCalendarId,
    start,
    end,
  }).map(({ name }) => name)
  const leave = useAbsencesForDate(date, { userId, start, end })
  const status = approval ? getApprovalStatus(approval) : undefined
  const { note } = useNoteForDate(date, { userId, start, end })

  const holidayAndLeaveData = new Map<ReactNode, ReactNode>()

  if (holidayNames.length > 0) {
    holidayAndLeaveData.set(
      <FontAwesomeIcon icon={faCalendarStar} />,
      holidayNames.join(', '),
    )
  }

  if (leave.length > 0) {
    leave.forEach((absence) => {
      holidayAndLeaveData.set(
        absence.type,
        absence.leave ? null : secondsToTimestamp(absence.duration),
      )
    })
  }

  return (
    <CellAnnotation
      className={className}
      lighten={status === Status.Sent}
      rightAnnotations={
        approval?.timeCard.sent_to_payroll && (
          <FontAwesomeIcon icon={faCloudCheck} size="sm" fixedWidth={true} />
        )
      }
      showLeaveIcon={leave.length > 0}
      showHolidayIcon={holidayNames.length > 0}
      showNotesIcon={Boolean(note)}
      tooltip={(id) => (
        <ApprovalTooltip
          extraData={holidayAndLeaveData}
          id={id}
          showRegularHoursTooltip={
            (approval?.totalDomainSecondsLogged ?? 0) > 0
          }
          timeCardId={approval?.timeCard.id}
        />
      )}
    >
      {approval && (
        <TimeStampCell
          className={twMerge(
            status === Status.Approved && 'bg-success-100',
            status === Status.Submitted && 'bg-warning-100',
          )}
          seconds={approval.totalDomainSecondsLogged}
          subSeconds={approval.totalDomainSecondsTagged}
          zeroMode="dash"
        />
      )}
    </CellAnnotation>
  )
}
